import { memo, useEffect, useRef } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import { CellMap } from './CellMap';
import { MapLegend, MapLayerToggle, MapTitle, MapZoom } from './MapOverlays';
import { KpiStrip } from './KpiStrip';

// Memoized: MapPane takes no props, so it renders once and is fully insulated
// from Dashboard re-renders (sidebar drags, layout state, the SSE alert stream).
// The map only updates via its children's own Redux/RTK-Query subscriptions.
export const MapPane = memo(function MapPane() {
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reflow the MapLibre canvas when its container changes width (sidebar drag,
  // collapse/expand, window resize). The GL drawing-buffer resize is DEBOUNCED
  // to fire once the size settles — calling map.resize() on every animation
  // frame re-projects the viewport each frame and makes the map shudder. While
  // the column animates, the canvas just scales to fill via CSS (see the
  // `.maplibregl-canvas` rule in theme-noc.css), so there's no gap in the
  // meantime and only one crisp re-projection at the end.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout>;
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => mapRef.current?.getMap()?.resize(), 120);
    });
    ro.observe(el);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        background: 'var(--surface-app)',
      }}
    >
      <div ref={containerRef} style={{ flex: 1, position: 'relative', minHeight: 0 }}>
        <CellMap mapRef={mapRef} />
        <MapTitle />
        <MapLayerToggle />
        <MapLegend />
        <MapZoom mapRef={mapRef} />
      </div>
      <KpiStrip />
    </div>
  );
});
